import { twMerge } from 'tailwind-merge';

import type { ButtonProps, BadgeProps } from '#ui/types';
import { breakpointsTailwind } from '@vueuse/core';
import lodashMapValues from 'lodash-es/mapValues';
import lodashMergeWith from 'lodash-es/mergeWith';

type BreakpointTailwind = keyof typeof breakpointsTailwind;

type ButtonResponsiveSize = 'xl';
type BadgeResponsiveSize = 'lg';
type ButtonUi = Partial<Record<keyof NonNullable<ButtonProps['ui']>, string>>;
type BadgeUi = Partial<Record<keyof NonNullable<BadgeProps['ui']>, string>>;

const addBreakpointPrefix = <T extends Record<string, string>>(ui: T, breakpoint: BreakpointTailwind): T => {
    return lodashMapValues(ui, classValue =>
        String(classValue)
            .split(' ')
            .map(value => `${breakpoint}:${value}`)
            .join(' ')
    ) as T;
};

export const mergeUi = <T extends Record<string, string | undefined>>(obj1: Partial<T>, obj2: Partial<T>): Partial<T> =>
    lodashMergeWith({}, obj1, obj2, (value1, value2) => (value1 && value2 ? twMerge(value1, value2) : (value2 ?? value1)));

export const buttonResponsiveSizeUi = (breakpoint: BreakpointTailwind, size: ButtonResponsiveSize): ButtonUi =>
    addBreakpointPrefix(
        (
            {
                xl: {
                    base: 'px-3 py-2 text-base gap-2',
                    leadingIcon: 'size-6',
                    leadingAvatarSize: 'xs',
                    trailingIcon: 'size-6',
                },
            } satisfies Record<ButtonResponsiveSize, ButtonUi>
        )[size],
        breakpoint
    );

export const badgeResponsiveSizeUi = (breakpoint: BreakpointTailwind, size: BadgeResponsiveSize): BadgeUi =>
    addBreakpointPrefix(
        (
            {
                lg: {
                    base: 'text-sm gap-1.5 rounded-full',
                    leadingIcon: 'size-5',
                    trailingIcon: 'size-5',
                },
            } satisfies Record<BadgeResponsiveSize, BadgeUi>
        )[size],
        breakpoint
    );
